/* eslint-disable @typescript-eslint/no-explicit-any */

export function getRealBsiItems(value: any) {
  return Array.isArray(value?.bsi)
    ? value.bsi.filter((item: any) => item?.id || item?.depth || item?.ppk)
    : []
}

export function getGraphDayState(value: any) {
  const planCode = Number(value?.plan || 0)
  const ppkLimit = value?.ppk_limit || null
  const ppkPending = value?.ppk_pending || null
  const bsiItems = getRealBsiItems(value)
  const ppkLimitDepth = normalizePpkDepth(ppkLimit?.depth)
  const ppkPendingDepth = normalizePpkDepth(ppkPending?.depth)
  const ppkPendingMinDepth = Math.max(0, ppkPendingDepth - 50)
  const bsiPpkDepth = bsiItems.reduce((depth: number, item: any) => (
    depth || normalizePpkDepth(item?.ppk)
  ), 0)
  const fullPassDepth = ppkLimitDepth || bsiPpkDepth || 900
  const fullPassMinDepth = Math.max(0, fullPassDepth - 5)
  const maxDepth = bsiItems.reduce((max: number, item: any) => (
    Math.max(max, Number(item.depth || 0))
  ), 0)
  const hasBsi = bsiItems.length > 0
  const hasFullPass = bsiItems.some((item: any) => {
    const itemPpkDepth = ppkLimitDepth || normalizePpkDepth(item?.ppk) || 900
    return Number(item?.depth || 0) >= Math.max(0, itemPpkDepth - 5)
  })
  const hasShortPass = hasBsi && !hasFullPass
  const manualDepth = Number(value?.depth || 0)
  const observedDepth = hasBsi ? maxDepth : manualDepth
  const hasManualFullPass = !hasBsi && manualDepth >= fullPassMinDepth
  const rawReason = value?.comment?.label || value?.comment?.name || value?.comment || ''
  const reason = rawReason
    || value?.comment?.name
    || value?.comment
    || ppkPending?.message
    || ''

  const result = (kind: string, depth = maxDepth) => ({
    kind,
    planCode,
    bsiItems,
    maxDepth: depth,
    fullPassDepth,
    fullPassMinDepth,
    ppkLimit,
    ppkPending,
    reason,
  })

  if (!value || typeof value !== 'object') {
    return result('empty')
  }

  if (
    ppkPending
    && ppkPendingDepth > 0
    && observedDepth >= ppkPendingMinDepth
    && observedDepth < 900
  ) {
    return result('ppk_pending', observedDepth)
  }

  if (!hasBsi && reason && !isPpkReason(reason)) {
    return result('reason')
  }

  if (planCode === 5 && manualDepth > 0) {
    return hasManualFullPass
      ? result('manual_planned', manualDepth)
      : result('no_pass', manualDepth)
  }

  if (planCode === 4 && hasShortPass) {
    return result('no_pass')
  }

  if (planCode === 4 && !hasBsi && manualDepth > 0) {
    return result('no_pass', manualDepth)
  }

  if (planCode === 2 && !hasBsi && manualDepth > 0) {
    return result('manual_planned', manualDepth)
  }

  if (planCode === 3 && hasFullPass) {
    return result('done_outside_plan')
  }

  if (planCode === 3 && !hasBsi && manualDepth > 0) {
    return result('manual_outside_plan', manualDepth)
  }

  if ((planCode === 1 || planCode === 2) && hasFullPass) {
    return result('done_planned')
  }

  if (hasFullPass) {
    return result('done')
  }

  if (hasShortPass) {
    return result('no_pass')
  }

  if (planCode === 1) {
    return result('planned')
  }

  return result('empty')
}

function normalizePpkDepth(value: any): number {
  const depth = Number(value || 0)

  if (!depth) {
    return 0
  }

  return depth === 1000 ? 900 : depth
}

export function getReasonAbbreviation(reason: any): string {
  const text = getReasonText(reason)

  if (!text) {
    return ''
  }

  const normalized = normalizeReasonText(text)
  const explicitMap: Record<string, string> = {
    'крс': 'КРС',
    'лар': 'ЛАР',
    'нет подъезда': 'НП',
    'нет подьезда': 'НП',
    'обрыв фрезы': 'ОФ',
    'скважина остановлена': 'СО',
    'подброс фрезы': 'ПФ',
    'поброс фрезы': 'ПФ',
    'заморожена буферная задвижка': 'ЗБЗ',
    'заморожена буфеерная задвижка': 'ЗБЗ',
    'отсутствие площадки лубрикатора': 'ОПЛ',
    'отсутствие площадки/лубрикатора': 'ОПЛ',
    'отсутствие площадки\\лубрикатора': 'ОПЛ',
  }

  if (explicitMap[normalized]) {
    return explicitMap[normalized]
  }

  return text
    .replace(/[\\/]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word: string) => word[0])
    .join('')
    .slice(0, 4)
    .toUpperCase()
}

export function isCriticalReason(reason: any): boolean {
  const normalized = normalizeReasonText(getReasonText(reason))
  return normalized.includes('обрыв фрезы')
    || normalized.includes('подброс фрезы')
    || normalized.includes('поброс фрезы')
}

function isPpkReason(reason: any): boolean {
  const normalized = normalizeReasonText(getReasonText(reason))
  return normalized === 'ппк' || normalized.includes('ппк')
}

export function getReasonStripeBackground(reason: any): string {
  if (isCriticalReason(reason)) {
    return 'repeating-linear-gradient(135deg, #d6bbfb 0, #d6bbfb 5px, #f4ebff 5px, #f4ebff 10px)'
  }

  return 'repeating-linear-gradient(135deg, #e4e7ec 0, #e4e7ec 5px, #f8fafc 5px, #f8fafc 10px)'
}

function getReasonText(reason: any): string {
  if (!reason) {
    return ''
  }

  if (typeof reason === 'string') {
    return reason
  }

  return String(reason.label || reason.name || reason.reason || '')
}

function normalizeReasonText(reason: string): string {
  return reason
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[\\/]+/g, ' ')
    .replace(/[.,;:!?()[\]{}"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
